import React from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import CommentThread from '../../../CommentThread';
import DisabledComment from '../../../DisabledComment';
import { getCommentableTypeForDeviationType } from '../../redux/helpers';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import PremiumGallery from '@wix/da-ds/pkg/Icons/24x24/PremiumGallery';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/16x16/EyeDisc2';
import { normalizePremiumFolderData } from '../../../utils/normalizePremiumFolderData';

import s from './Comments.scss';

export const COMMENTS_HTML_ID = 'comments';

export interface Props {
  deviation: PapiDeviation;
  isOwner: boolean;
  className?: string;
}

export const Comments: React.FC<React.PropsWithChildren<Props>> = ({
  deviation: { deviationId, isCommentable, hasPrivateComments },
  deviation,
  isOwner,
  className,
}) => {
  const { t } = useTranslation();
  const commentableTypeId = getCommentableTypeForDeviationType(deviation);
  const { isPremium, isPaidFolder } = normalizePremiumFolderData(
    deviation?.premiumFolderData
  );
  const ownerInfo = isOwner && isPremium && hasPrivateComments && (
    <div className={s['owner-info-wrapper']}>
      {isPaidFolder ? (
        <PremiumGallery size={IconSize.TINY} />
      ) : (
        <EyeDisc2 size={IconSize.TINY} />
      )}
      <span className={s['owner-info']}>
        {t(
          isPaidFolder
            ? 'deviation.comments.paywallOwner.premium'
            : 'deviation.comments.paywallOwner.watchers_only'
        )}
      </span>
    </div>
  );

  return (
    <ErrorBoundary debugComponent="Comments">
      <BiLoggerContextProvider value={{ sectionname: 'comments' }}>
        <div className={s['comments-anchor']} id={COMMENTS_HTML_ID} />
        <CommentThread
          key={`${commentableTypeId}:${deviationId}`}
          commentableTypeId={commentableTypeId}
          commentableItemId={deviationId}
          postbox={isCommentable ? 'both' : 'none'}
          statsAndControls="on"
          className={className}
          allowSelfInit={false}
          enableOffsetTracking
          extraInHeader={ownerInfo}
          isSticky
        />
        {!isCommentable && (
          <DisabledComment
            className={classnames(s['comments-disabled'], className)}
            title={t('deviation.comments.commentsDisabled')}
          />
        )}
      </BiLoggerContextProvider>
    </ErrorBoundary>
  );
};
Comments.displayName = 'Comments';

export default Comments;
